import { useState } from "react";
import { View, Text, Tooltip, Icon } from "../base";
import { JsonViewer } from "../base/JsonViewer";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { SpanEventsContent } from "./SpanEventsContent";
import { CompletionRequest, CompletionResponse, parseCompletionRequest, parseCompletionResponse } from "../../types/completionRequest";
import { formatUSD } from "../../utils/billing";
import { safeString } from "../../utils/strings";

/**
 * Detail view for the voicerun-completions-proxy outer "completion" span
 * (voicerun.span.type = "proxy.completion"). Renders the inbound request body
 * — system instruction, messages, tool definitions, fallbacks, parameters
 * — that the handler actually POSTed, alongside cost / events / raw
 * attributes.
 *
 * Distinct from CompletionSpanDetailView, which renders the provider-side
 * OpenInference-instrumented SDK call. This view is the request-level
 * "what the handler asked for"; that view is the SDK-level "what actually
 * went on the wire to OpenAI/Anthropic/etc."
 */
interface ProxyCompletionSpanDetailViewProps {
  attributes: Record<string, unknown>;
  rawData: RawSpanData;
  className?: string;
}

// Every message in the Request view is an input to the LLM regardless of
// role; chat-style left/right alignment doesn't apply here. Render all
// bubbles full-width and left-aligned, differentiating only by background
// tint + role label.
const ROLE_BUBBLE_CLASSES: Record<string, string> = {
  system: "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800",
  user: "bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800",
  assistant: "bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700",
  tool: "bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800",
};
const FALLBACK_BUBBLE_CLASS = "bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700";

// OpenAI-shaped tool call: { id, type: "function", function: { name, arguments: string } }.
// The proxy normalizes Anthropic / Vertex tool_use blocks into this shape via
// message.serialize(), so we only need to handle one variant here.
interface ToolCallShape {
  id?: string;
  type?: string;
  function?: { name?: string; arguments?: string | Record<string, unknown> };
  [k: string]: unknown;
}

// Renders a single tool call as a card: header with the function name (and
// id, if present) over a JsonViewer of the parsed arguments. Falls back to
// rendering arguments as a raw string if they aren't valid JSON.
const ToolCallBlock = ({ toolCall }: { toolCall: ToolCallShape }) => {
  const fn = toolCall.function ?? {};
  const name = typeof fn.name === "string" && fn.name ? fn.name : "(unnamed)";
  const argsRaw = fn.arguments;
  let argsParsed: unknown = null;
  let argsString: string | null = null;
  if (typeof argsRaw === "string") {
    try {
      argsParsed = JSON.parse(argsRaw);
    } catch {
      argsString = argsRaw;
    }
  } else if (argsRaw && typeof argsRaw === "object") {
    argsParsed = argsRaw;
  }
  return (
    <View className="bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
      <View className="px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <View className="flex flex-row items-center justify-between gap-2">
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">tool call</Text>
          {toolCall.id && <Text className="text-xs font-mono text-gray-400 dark:text-gray-500 truncate">{toolCall.id}</Text>}
        </View>
        <Text className="text-sm font-mono font-medium text-gray-800 dark:text-gray-200 truncate block mt-2">{name}</Text>
      </View>
      {argsParsed !== null ? (
        <JsonViewer
          data={argsParsed}
          collapsed={5}
          className="p-2"
        />
      ) : argsString !== null ? (
        <Text className="text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-pre-wrap p-2 block">{argsString}</Text>
      ) : (
        <Text className="text-xs italic text-gray-400 dark:text-gray-500 p-2 block">no arguments</Text>
      )}
    </View>
  );
};

const ProxyMessageBubble = ({ message }: { message: { role?: string; content?: string; tool_calls?: unknown[]; [k: string]: unknown } }) => {
  const role = typeof message.role === "string" ? message.role : "unknown";
  const content = typeof message.content === "string" ? message.content : undefined;
  const toolCalls = Array.isArray(message.tool_calls) ? (message.tool_calls as ToolCallShape[]) : undefined;
  const bubbleClass = ROLE_BUBBLE_CLASSES[role] ?? FALLBACK_BUBBLE_CLASS;
  const hasContent = typeof content === "string" && content.length > 0;
  const hasToolCalls = !!toolCalls && toolCalls.length > 0;
  const hasRenderableBody = hasContent || hasToolCalls;
  return (
    <View className={`rounded-lg px-3 py-2 mb-2 ${bubbleClass}`}>
      <View className="mb-1">
        <Text className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">{role}</Text>
      </View>
      {hasContent && <Text className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{safeString(content)}</Text>}
      {hasToolCalls && (
        <View className={`space-y-2 ${hasContent ? "mt-2" : ""}`}>
          {toolCalls!.map((tc, i) => (
            <ToolCallBlock
              key={i}
              toolCall={tc as ToolCallShape}
            />
          ))}
        </View>
      )}
      {!hasRenderableBody && (
        <View className="bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 overflow-hidden mt-1">
          <JsonViewer
            data={message}
            collapsed={5}
            className="p-2"
          />
        </View>
      )}
    </View>
  );
};

const OverviewRow = ({ label, value, tooltip }: { label: string; value: React.ReactNode; tooltip?: React.ReactNode }) => (
  <View className="flex flex-row items-center gap-3 py-1.5 border-b border-gray-100 dark:border-gray-800">
    <View className="flex flex-row items-center gap-1 w-48 shrink-0">
      <Text className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</Text>
      {tooltip && (
        <Tooltip
          content={tooltip}
          placement="top"
        >
          <View className="cursor-help text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <Icon
              name="info"
              size={12}
            />
          </View>
        </Tooltip>
      )}
    </View>
    <Text className="text-sm text-gray-700 dark:text-gray-300">{value}</Text>
  </View>
);

const NullValue = () => <span className="text-gray-400 dark:text-gray-500 italic">null</span>;

// Renders the request body verbatim — what the handler asked for.
// Provider / model here come from the request body (the *intended* values),
// not from the proxy span's overwritten completion.provider / completion.model
// (which reflect the *winning* attempt). For fallback-aware "responding"
// provider/model, see ResponsePanel.
const RequestParametersPanel = ({ request, attributes }: { request: CompletionRequest; attributes: Record<string, unknown> }) => {
  const vrKeyUsed = attributes["voicerun.vr_key_used"];
  const requestId = typeof attributes["voicerun.request_id"] === "string" ? (attributes["voicerun.request_id"] as string) : null;

  return (
    <View className="space-y-4">
      <View>
        <OverviewRow
          label="Provider"
          value={<span className="font-medium text-gray-800 dark:text-gray-200">{request.provider}</span>}
        />
        <OverviewRow
          label="Model"
          value={<span className="font-medium text-gray-800 dark:text-gray-200">{request.model}</span>}
        />
        <OverviewRow
          label="Streaming"
          value={<span className="font-mono">{String(request.streaming ?? false)}</span>}
        />
        <OverviewRow
          label="Key"
          value={
            typeof vrKeyUsed === "boolean" ? (
              <span className="font-medium text-gray-800 dark:text-gray-200">{vrKeyUsed ? "VoiceRun" : "Customer"}</span>
            ) : (
              <NullValue />
            )
          }
        />
        <OverviewRow
          label="Request ID"
          value={requestId ? <span className="font-mono text-xs">{requestId}</span> : <NullValue />}
        />
        <OverviewRow
          label="Temperature"
          value={typeof request.temperature === "number" ? <span className="font-mono">{request.temperature}</span> : <NullValue />}
        />
        <OverviewRow
          label="Max tokens"
          value={typeof request.max_tokens === "number" ? <span className="font-mono">{request.max_tokens}</span> : <NullValue />}
        />
        <OverviewRow
          label="Tool choice"
          value={typeof request.tool_choice === "string" ? <span className="font-mono">{request.tool_choice}</span> : <NullValue />}
        />
        <OverviewRow
          label="Timeout (s)"
          value={typeof request.timeout === "number" ? <span className="font-mono">{request.timeout}</span> : <NullValue />}
        />
      </View>

      {/* Heavy JSON-shaped fields render below the row table when present.
          Skip when null — including these as "null" rows would just be
          dead space. */}
      {request.response_schema && (
        <View>
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">Response schema</Text>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <JsonViewer
              data={request.response_schema}
              collapsed={5}
              className="p-2"
            />
          </View>
        </View>
      )}
      {request.stream_options && (
        <View>
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">Stream options</Text>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <JsonViewer
              data={request.stream_options}
              collapsed={5}
              className="p-2"
            />
          </View>
        </View>
      )}
      {request.retry && (
        <View>
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">Retry</Text>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <JsonViewer
              data={request.retry}
              collapsed={5}
              className="p-2"
            />
          </View>
        </View>
      )}
      {request.provider_kwargs && (
        <View>
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">Provider kwargs</Text>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <JsonViewer
              data={request.provider_kwargs}
              collapsed={5}
              className="p-2"
            />
          </View>
        </View>
      )}
    </View>
  );
};

const MessagesPanel = ({ request }: { request: CompletionRequest }) => {
  if (!request.messages || request.messages.length === 0) {
    return <Text className="text-sm text-gray-500 dark:text-gray-400">No messages.</Text>;
  }
  return (
    <View>
      {request.messages.map((msg, i) => (
        <ProxyMessageBubble
          key={i}
          message={msg as { role?: string; content?: string }}
        />
      ))}
    </View>
  );
};

const ToolsPanel = ({ request }: { request: CompletionRequest }) => {
  if (!request.tools || request.tools.length === 0) {
    return <Text className="text-sm text-gray-500 dark:text-gray-400">No tools.</Text>;
  }
  return (
    <View className="space-y-3">
      {request.tools.map((tool, i) => {
        const fn = tool.function;
        return (
          <View key={i}>
            <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{fn?.name || "(unnamed)"}</Text>
            {fn?.description && <Text className="text-xs text-gray-600 dark:text-gray-400 mt-1 block whitespace-pre-wrap">{fn.description}</Text>}
            {fn?.parameters && (
              <View className="mt-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                <JsonViewer
                  data={fn.parameters}
                  collapsed={5}
                  className="p-2"
                />
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
};

// Fields stripped from each fallback before rendering: api_key is removed
// at trace-stamp time on the proxy side, but we exclude it defensively
// here too in case future scrubbing rules change.
const FALLBACK_HEADER_KEYS = new Set(["provider", "model", "api_key"]);

const FallbacksPanel = ({ request }: { request: CompletionRequest }) => {
  if (!request.fallbacks || request.fallbacks.length === 0) {
    return <Text className="text-sm text-gray-500 dark:text-gray-400">No fallbacks configured.</Text>;
  }
  return (
    <View className="space-y-4">
      {request.fallbacks.map((fb, i) => {
        const provider = typeof fb.provider === "string" ? fb.provider : "(provider unset)";
        const model = typeof fb.model === "string" ? fb.model : "";
        const overrides: Record<string, unknown> = {};
        Object.entries(fb).forEach(([key, value]) => {
          if (FALLBACK_HEADER_KEYS.has(key)) return;
          overrides[key] = value;
        });
        const hasOverrides = Object.keys(overrides).length > 0;
        return (
          <View key={i}>
            <View className="flex flex-row items-baseline gap-2">
              <Text className="text-xs font-mono text-gray-500 dark:text-gray-400">{i + 1}.</Text>
              <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {provider}
                {model ? ` / ${model}` : ""}
              </Text>
              {!hasOverrides && <Text className="text-xs text-gray-400 dark:text-gray-500 italic">no overrides</Text>}
            </View>
            {hasOverrides && (
              <View className="mt-2 ml-6 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
                <JsonViewer
                  data={overrides}
                  collapsed={5}
                  className="p-2"
                />
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
};

// Pulls TTFT and per-attempt duration off the most recent successful
// completion.attempt event, since the proxy emits those on the event
// rather than as top-level span attributes.
const findSuccessfulAttempt = (events: NonNullable<RawSpanData["events"]>) =>
  [...events].reverse().find((e) => e.attributes["event.success"] === true && e.name === "completion.attempt");

// Provider / model here come from completion.provider and completion.model
// on the proxy span, which the proxy *overwrites* with the winning attempt's
// values when a fallback fires. So these reflect what actually generated the
// response, not what the request body asked for. The assistant message
// itself is parsed out of the completion.response JSON blob.
const ResponsePanel = ({ attributes }: { attributes: Record<string, unknown> }) => {
  const provider = typeof attributes["completion.provider"] === "string" ? (attributes["completion.provider"] as string) : null;
  const model = typeof attributes["completion.model"] === "string" ? (attributes["completion.model"] as string) : null;
  const finishReason = typeof attributes["completion.finish_reason"] === "string" ? (attributes["completion.finish_reason"] as string) : null;
  const response: CompletionResponse | null = parseCompletionResponse(attributes["completion.response"]);
  const message = response?.message ?? null;
  return (
    <View className="space-y-4">
      <View>
        <OverviewRow
          label="Provider"
          value={provider ? <span className="font-medium text-gray-800 dark:text-gray-200">{provider}</span> : <NullValue />}
        />
        <OverviewRow
          label="Model"
          value={model ? <span className="font-medium text-gray-800 dark:text-gray-200">{model}</span> : <NullValue />}
        />
        <OverviewRow
          label="Finish Reason"
          value={finishReason ? <span className="font-mono text-gray-800 dark:text-gray-200">{finishReason}</span> : <NullValue />}
        />
      </View>
      {message && <ProxyMessageBubble message={message as { role?: string; content?: string; tool_calls?: unknown[]; [k: string]: unknown }} />}
    </View>
  );
};

const MetricsPanel = ({ attributes, rawData }: { attributes: Record<string, unknown>; rawData: RawSpanData }) => {
  const inputTokens = typeof attributes["completion.usage.input_tokens"] === "number" ? (attributes["completion.usage.input_tokens"] as number) : null;
  const outputTokens = typeof attributes["completion.usage.output_tokens"] === "number" ? (attributes["completion.usage.output_tokens"] as number) : null;
  const cacheReadTokens =
    typeof attributes["completion.usage.cache_read_tokens"] === "number" ? (attributes["completion.usage.cache_read_tokens"] as number) : null;
  const cacheWriteTokens =
    typeof attributes["completion.usage.cache_write_tokens"] === "number" ? (attributes["completion.usage.cache_write_tokens"] as number) : null;
  const costUsd = typeof attributes["cost.usd"] === "number" ? (attributes["cost.usd"] as number) : null;

  const successfulEvent = findSuccessfulAttempt(rawData.events ?? []);
  const durationMs =
    successfulEvent && typeof successfulEvent.attributes["event.duration_ms"] === "number" ? (successfulEvent.attributes["event.duration_ms"] as number) : null;
  const ttftMs =
    successfulEvent && typeof successfulEvent.attributes["event.ttft_ms"] === "number" ? (successfulEvent.attributes["event.ttft_ms"] as number) : null;
  // TTFC is a top-level span attr (not on the attempt event) because it's
  // measured at the proxy's outbound boundary and is per-request, not
  // per-attempt — only one chunk ever reaches the caller regardless of
  // how many fallback attempts ran upstream.
  const ttfcMs = typeof attributes["completion.ttfc_ms"] === "number" ? (attributes["completion.ttfc_ms"] as number) : null;

  return (
    <View>
      <OverviewRow
        label="Cost"
        value={costUsd !== null ? <span className="font-mono text-gray-800 dark:text-gray-200">{formatUSD(costUsd)}</span> : <NullValue />}
      />
      <OverviewRow
        label="Input Tokens"
        value={inputTokens !== null ? <span className="font-mono">{inputTokens.toLocaleString()}</span> : <NullValue />}
      />
      <OverviewRow
        label="Output Tokens"
        value={outputTokens !== null ? <span className="font-mono">{outputTokens.toLocaleString()}</span> : <NullValue />}
      />
      <OverviewRow
        label="Cache Read"
        value={cacheReadTokens !== null ? <span className="font-mono">{cacheReadTokens.toLocaleString()}</span> : <NullValue />}
      />
      <OverviewRow
        label="Cache Write"
        value={cacheWriteTokens !== null ? <span className="font-mono">{cacheWriteTokens.toLocaleString()}</span> : <NullValue />}
      />
      <OverviewRow
        label="Duration"
        value={durationMs !== null ? <span className="font-mono">{Math.round(durationMs)}ms</span> : <NullValue />}
      />
      <OverviewRow
        label="Time to First Token"
        value={ttftMs !== null ? <span className="font-mono">{Math.round(ttftMs)}ms</span> : <NullValue />}
        tooltip={
          <View className="p-2 max-w-xs">
            <Text className="text-xs text-gray-700 dark:text-gray-200 whitespace-normal">
              Time from the start of the winning attempt to the first raw content delta from the upstream provider. Captured before sentence batching, so it
              reflects provider responsiveness independent of stream options.
            </Text>
          </View>
        }
      />
      <OverviewRow
        label="Time to First Chunk"
        value={ttfcMs !== null ? <span className="font-mono">{Math.round(ttfcMs)}ms</span> : <NullValue />}
        tooltip={
          <View className="p-2 max-w-xs">
            <Text className="text-xs text-gray-700 dark:text-gray-200 whitespace-normal">
              Time from request entry to the first chunk yielded back to the caller. Includes any sentence batching and time spent on failed fallback attempts.
              For non-streaming requests this equals the full request time.
            </Text>
          </View>
        }
      />
    </View>
  );
};

interface NavItem {
  id: string;
  label: string;
  count?: number;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

// Two-group rail: INPUT contains the request-side panels, OUTPUT contains
// the response-side panels (response data + computed metrics). Both groups
// have multiple items, so the heading-plus-items pattern lands consistently
// across both — no single-item awkwardness.
const CompletionLeftNav = ({ sections, activeId, onSelect }: { sections: NavSection[]; activeId: string; onSelect: (id: string) => void }) => (
  <View className="flex flex-col w-48 shrink-0 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
    {sections.map((section, sectionIdx) => (
      <View
        key={section.label}
        className={sectionIdx > 0 ? "mt-4" : ""}
      >
        <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 px-3 mb-1">{section.label}</Text>
        <View className="flex flex-col gap-0.5">
          {section.items.map((item) => {
            const isActive = item.id === activeId;
            return (
              <button
                key={item.id}
                onClick={() => onSelect(item.id)}
                className={`flex flex-row items-center justify-between px-3 py-1.5 rounded text-sm text-left transition-colors ${
                  isActive
                    ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <span>{item.label}</span>
                {item.count !== undefined && (
                  <span className={`text-xs ${isActive ? "text-indigo-500 dark:text-indigo-400" : "text-gray-400 dark:text-gray-500"}`}>{item.count}</span>
                )}
              </button>
            );
          })}
        </View>
      </View>
    ))}
  </View>
);

const CompletionContent = ({ request, attributes, rawData }: { request: CompletionRequest; attributes: Record<string, unknown>; rawData: RawSpanData }) => {
  // Two groups: INPUT (request-side panels) and OUTPUT (response-side
  // panels + computed metrics). Both have multiple items, so the
  // heading-plus-items pattern is consistent across the rail.
  const sections: NavSection[] = [
    {
      label: "Input",
      items: [
        { id: "request-parameters", label: "Parameters" },
        { id: "messages", label: "Messages", count: request.messages?.length ?? 0 },
        ...(request.tools && request.tools.length > 0 ? [{ id: "tools", label: "Tools", count: request.tools.length }] : []),
        ...(request.fallbacks && request.fallbacks.length > 0 ? [{ id: "fallbacks", label: "Fallbacks", count: request.fallbacks.length }] : []),
      ],
    },
    {
      label: "Output",
      items: [
        { id: "response", label: "Response" },
        { id: "metrics", label: "Metrics" },
      ],
    },
  ];
  const [activeId, setActiveId] = useState(sections[0].items[0].id);

  // Rail does NOT live inside the right-side scroll container, so it stays
  // pinned and its border-r runs the full height of the panel.
  return (
    <View className="flex-1 flex flex-row overflow-hidden">
      <CompletionLeftNav
        sections={sections}
        activeId={activeId}
        onSelect={setActiveId}
      />
      <View className="flex-1 min-w-0 overflow-y-auto p-4">
        {activeId === "request-parameters" && (
          <RequestParametersPanel
            request={request}
            attributes={attributes}
          />
        )}
        {activeId === "messages" && <MessagesPanel request={request} />}
        {activeId === "tools" && <ToolsPanel request={request} />}
        {activeId === "fallbacks" && <FallbacksPanel request={request} />}
        {activeId === "response" && <ResponsePanel attributes={attributes} />}
        {activeId === "metrics" && (
          <MetricsPanel
            attributes={attributes}
            rawData={rawData}
          />
        )}
      </View>
    </View>
  );
};

export const ProxyCompletionSpanDetailView = ({ attributes, rawData, className }: ProxyCompletionSpanDetailViewProps) => {
  const request = parseCompletionRequest(attributes["completion.request"]);
  const events = rawData.events ?? [];

  const tabs: TabDefinition[] = [
    ...(request ? [{ id: "completion", label: "Completion", color: "indigo" as const }] : []),
    ...(events.length > 0 ? [{ id: "events", label: `Events (${events.length})`, color: "amber" as const }] : []),
    { id: "attributes", label: "Attributes", color: "gray" },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0].id);

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      {/* The Completion tab manages its own layout (left rail + scrolling
          right pane) so the rail stays fixed and its border runs full
          height. Other tabs use a simple padded scroll container. */}
      {activeTab === "completion" && request && (
        <CompletionContent
          request={request}
          attributes={attributes}
          rawData={rawData}
        />
      )}
      {activeTab === "events" && (
        <View className="flex-1 overflow-y-auto p-4">
          <SpanEventsContent events={events} />
        </View>
      )}
      {activeTab === "attributes" && (
        <View className="flex-1 overflow-y-auto p-4">
          <RawSpanDetailView data={rawData} />
        </View>
      )}
    </View>
  );
};
